export const helpCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'PlayCircle',
    description: 'Learn the basics of PaceEstate platform',
    articles: [
      {
        id: 'first-login',
        title: 'Your First Login',
        lastUpdated: '2025-12-01',
        content: `
# Your First Login

Welcome to PaceEstate! This guide will help you get started with your first login and initial setup.

## Accessing the Platform

1. Navigate to your PaceEstate login page
2. Enter your email address and password provided by your administrator
3. Click "Sign In" to access your dashboard

## First-Time Setup

After your first login, you'll see:

- **Dashboard Overview**: Quick statistics and recent activity
- **Navigation Menu**: Access all features from the left sidebar
- **Profile Icon**: Top right corner for account settings

## What to Do First

1. **Update Your Profile**: Click your avatar in the top right and select "Profile"
   - Add your full name
   - Upload a profile photo
   - Set your contact preferences

2. **Familiarize with Navigation**: Explore the main menu items:
   - Dashboard: Your home base
   - Sales: Manage estate sales
   - Inventory: Track items
   - Calendar: Schedule events
   - Leads: Manage potential clients

3. **Check Notifications**: Review any pending tasks or assignments

## Getting Help

- Click the help icon (?) in the top navigation bar
- Use the search feature to find specific topics
- Contact your administrator for role-specific questions

## Next Steps

- Read the Dashboard Overview guide
- Learn about your specific role permissions
- Explore the features relevant to your daily tasks
        `,
        category: 'getting-started',
        relatedArticles: ['dashboard-overview', 'navigation', 'user-roles']
      },
      {
        id: 'dashboard-overview',
        title: 'Dashboard Overview',
        lastUpdated: '2025-12-01',
        content: `
# Dashboard Overview

Your dashboard is the central hub for all PaceEstate activities. Here's what you'll find.

## Dashboard Widgets

### Quick Statistics
At the top of your dashboard, you'll see key metrics:
- **Active Sales**: Number of ongoing estate sales
- **Total Revenue**: Current month's revenue
- **Pending Invoices**: Outstanding invoices requiring attention
- **Active Leads**: Potential clients in your pipeline

### Recent Activity Feed
The activity feed shows:
- New leads and client inquiries
- Upcoming appointments and events
- Recent inventory additions
- Payment notifications
- Commission calculations

### Upcoming Events
View your schedule at a glance:
- Estate sale dates and times
- Client appointments
- Staff meetings
- Important deadlines

### Quick Actions
Common tasks accessible from the dashboard:
- Create new sale
- Add inventory item
- Schedule appointment
- Generate invoice
- Enter payment

## Customizing Your Dashboard

Administrators can customize which widgets appear on the dashboard:
1. Go to Settings > Dashboard Settings
2. Enable/disable widgets
3. Rearrange widget order
4. Set default date ranges for statistics

## Filtering Data

Use the date picker to filter statistics:
- This Week
- This Month
- This Quarter
- Custom Date Range

## Mobile Access

The dashboard is fully responsive and optimized for tablets and phones. All key metrics and quick actions are accessible on mobile devices.

## Performance Tips

- Dashboard data refreshes automatically every 5 minutes
- Click the refresh icon to manually update
- Large data sets may take a few seconds to load
        `,
        category: 'getting-started',
        relatedArticles: ['first-login', 'navigation', 'mobile-access']
      },
      {
        id: 'navigation',
        title: 'Navigating the App',
        lastUpdated: '2025-12-01',
        content: `
# Navigating the App

PaceEstate is organized to make your workflow efficient. Here's how to navigate effectively.

## Main Navigation Menu

The left sidebar contains all major sections:

### Core Operations
- **Dashboard**: Your home base with key metrics
- **Sales**: Create and manage estate sales
- **Inventory**: Track and manage items
- **Calendar**: Schedule and view events
- **Appointments**: Manage client meetings

### Client Management
- **Leads**: Potential clients and inquiries
- **Clients**: Active client database
- **Contact Requests**: Public website inquiries

### Financial
- **Invoices**: Create and track invoices
- **Payments**: Record and manage payments
- **Commissions**: Staff commission tracking

### Resources
- **Documents**: Contract templates and files
- **Staff Schedule**: Team availability

### Administration (Admin Only)
- **Reports**: Analytics and insights
- **Settings**: System configuration
- **Users**: Staff management
- **Email Templates**: Automated communications

## Top Navigation Bar

### Global Search
The search icon allows you to quickly find:
- Sales by client name or address
- Inventory items by description
- Leads by name or phone
- Invoices by number
- Any record in the system

### Notifications
Bell icon shows:
- New leads and inquiries
- Upcoming appointments
- Payment reminders
- System alerts

### Theme Toggle
Switch between light and dark mode for comfortable viewing in any environment.

### Profile Menu
Access your account:
- View/edit profile
- Change password
- Logout

## Keyboard Shortcuts

Speed up your workflow with shortcuts:
- **Ctrl/Cmd + K**: Open global search
- **Ctrl/Cmd + /**: Open help
- **Esc**: Close dialogs and modals

## Breadcrumbs

When viewing detailed pages, breadcrumbs at the top show your location and allow quick navigation back to previous pages.

## Mobile Navigation

On mobile devices:
- Tap the hamburger menu to access navigation
- Swipe left to close the menu
- All features are accessible but optimized for smaller screens
        `,
        category: 'getting-started',
        relatedArticles: ['dashboard-overview', 'global-search', 'keyboard-shortcuts']
      },
      {
        id: 'user-roles',
        title: 'Understanding User Roles',
        lastUpdated: '2025-12-01',
        content: `
# Understanding User Roles

PaceEstate uses role-based access control to ensure team members see only what they need.

## Role Types

### Administrator
Full system access including:
- All sales and inventory operations
- Financial management (invoices, payments, commissions)
- System settings and configuration
- User management
- Reports and analytics
- Email template management

### Manager
Operations management access:
- Create and manage sales
- Full inventory control
- Client and lead management
- View reports (limited)
- Schedule management

### Sales Staff
Day-to-day operations:
- View assigned sales
- Add/edit inventory for assigned sales
- Record payments (for their sales)
- View their commissions
- Manage their appointments

### Viewing Only
Read-only access:
- View sales information
- View inventory
- View reports
- Cannot create or modify records

## Checking Your Role

To see your current role and permissions:
1. Click your profile picture in the top right
2. Select "Profile"
3. Your role is displayed under your name

## Permission Examples

### What Admins Can Do (Others Cannot)
- Add/remove users
- Change system settings
- Configure tax rates and commission structures
- Access all financial reports
- Modify email templates
- Delete records permanently

### What All Users Can Do
- Update their own profile
- View their assigned tasks
- Access help documentation
- Receive notifications
- Use global search

## Requesting Access

If you need access to features not available in your role:
1. Contact your administrator
2. Explain which features you need and why
3. Administrator will review and adjust your role if appropriate

## Security Best Practices

Regardless of your role:
- Never share your login credentials
- Log out when using shared computers
- Report suspicious activity to your administrator
- Keep your password strong and unique
        `,
        category: 'getting-started',
        relatedArticles: ['first-login', 'security-best-practices']
      }
    ]
  },
  {
    id: 'managing-sales',
    title: 'Managing Sales',
    icon: 'ShoppingCart',
    description: 'Create and manage estate sales',
    articles: [
      {
        id: 'creating-sale',
        title: 'Creating a New Sale',
        lastUpdated: '2025-12-01',
        content: `
# Creating a New Sale

Estate sales are the core of your business. Here's how to create and set them up properly.

## Starting a New Sale

1. Navigate to **Sales** in the main menu
2. Click the **"Create New Sale"** button (top right)
3. Fill out the sale creation form

## Required Information

### Basic Details
- **Client Name**: Select from existing clients or create new
- **Sale Address**: Property address where sale will occur
- **Sale Date**: Start date of the sale
- **End Date**: Last day of the sale (multi-day sales)

### Financial Settings
- **Commission Rate**: Percentage you'll charge (default from settings)
- **Tax Rate**: Sales tax percentage for the jurisdiction
- **Payment Terms**: When payment is due from client

### Sale Status
- **Draft**: Sale is being planned, not visible publicly
- **Scheduled**: Sale is confirmed and can be published
- **Active**: Sale is currently happening
- **Completed**: Sale has ended
- **Cancelled**: Sale was cancelled

## Optional Details

### Description
Provide a compelling description that will appear on the public website:
- Highlight unique or valuable items
- Mention the property type (estate, downsizing, etc.)
- Include special features (antiques, collectibles, furniture)

### Photos
Add photos to attract buyers:
- Click "Upload Photos" to add images
- First photo becomes the featured image
- Recommended: 5-10 high-quality photos
- Show variety of items and property

### Sale Hours
Set operating hours for each day:
- Click "Add Hours" for each sale day
- Example: Friday 9:00 AM - 4:00 PM, Saturday 9:00 AM - 2:00 PM
- Hours appear on public listing

## After Creating the Sale

Once created, you can:
1. **Add Inventory**: Start cataloging items for the sale
2. **Assign Staff**: Add team members to help with the sale
3. **Generate Marketing Materials**: Create flyers and social posts
4. **Publish to Website**: Make the sale visible to public

## Best Practices

- Create sales as soon as contracts are signed
- Keep status updated (Draft → Scheduled → Active → Completed)
- Add photos before publishing to public website
- Review commission and tax rates before completing sale
- Document special terms or client requests in notes

## Common Mistakes to Avoid

- Don't forget to set the correct sale dates
- Ensure tax rate matches the property jurisdiction
- Verify commission rate with client contract
- Don't publish without adding inventory and photos
        `,
        category: 'managing-sales',
        relatedArticles: ['sale-statuses', 'publishing-sales', 'adding-inventory']
      },
      {
        id: 'sale-statuses',
        title: 'Sale Statuses Explained',
        lastUpdated: '2025-12-01',
        content: `
# Sale Statuses Explained

Understanding sale statuses helps you manage your workflow and keep clients informed.

## Status Types

### Draft
**When to Use**: Initial planning phase

**Characteristics**:
- Not visible on public website
- Can make unlimited changes
- No financial calculations yet
- Perfect for planning and preparation

**Typical Duration**: 1-4 weeks before sale date

**Next Steps**: Add inventory, photos, and details. Move to Scheduled when ready to promote.

### Scheduled
**When to Use**: Sale is confirmed and ready to promote

**Characteristics**:
- Can be published to public website
- Inventory can be added/edited
- Marketing materials can be generated
- Visible to staff and optionally public

**Typical Duration**: 1-3 weeks before sale date

**Next Steps**: Continue adding inventory, promote sale, prepare property.

### Active
**When to Use**: Sale is currently in progress

**Characteristics**:
- Sale dates are current or recent
- Buyers are shopping
- Items are being sold and removed
- Inventory status updates happening
- Payments being collected

**Typical Duration**: 1-3 days (length of sale)

**Next Steps**: Track sales, mark items sold, collect payments, manage buyers.

### Completed
**When to Use**: Sale has ended

**Characteristics**:
- All sale days have passed
- Final inventory reconciliation
- Commission calculations finalized
- Invoice generated for client
- Reports available

**Typical Duration**: Permanent final status

**Next Steps**: Generate final reports, create invoice, schedule cleanup/donation.

### Cancelled
**When to Use**: Sale will not proceed

**Characteristics**:
- Client cancelled contract
- Sale postponed indefinitely
- Not visible publicly
- Inventory preserved but sale inactive

**Next Steps**: Document cancellation reason, handle any deposits/fees.

## Changing Status

To update a sale status:
1. Open the sale detail page
2. Click the current status badge
3. Select new status from dropdown
4. Confirm the change
5. System automatically updates visibility and calculations

## Status Workflow

Typical progression:
\`\`\`
Draft → Scheduled → Active → Completed
\`\`\`

Alternative paths:
\`\`\`
Draft → Cancelled
Scheduled → Cancelled
\`\`\`

## Automated Status Changes

The system automatically:
- Changes Scheduled to Active when sale start date arrives
- Suggests changing Active to Completed after end date passes
- Sends notifications on status changes

## Status Impact on Features

| Feature | Draft | Scheduled | Active | Completed | Cancelled |
|---------|-------|-----------|--------|-----------|-----------|
| Public Visibility | No | Optional | Yes | Yes* | No |
| Add Inventory | Yes | Yes | Yes | View Only | View Only |
| Edit Details | Yes | Yes | Limited | No | No |
| Generate Invoice | No | No | Yes | Yes | No |
| Marketing Materials | No | Yes | Yes | No | No |

*Completed sales shown in archive

## Best Practices

- Move from Draft to Scheduled at least 2 weeks before sale
- Update to Active on the morning of first sale day
- Mark Completed within 24 hours of sale ending
- Document cancellation reasons for business insights
        `,
        category: 'managing-sales',
        relatedArticles: ['creating-sale', 'publishing-sales', 'sale-workflow']
      },
      {
        id: 'publishing-sales',
        title: 'Publishing Sales to Website',
        lastUpdated: '2025-12-01',
        content: `
# Publishing Sales to Website

Make your sales visible to potential buyers through the public website.

## Before Publishing Checklist

Ensure your sale has:
- [ ] Status set to "Scheduled" or "Active"
- [ ] Compelling description (2-3 paragraphs)
- [ ] At least 5 high-quality photos
- [ ] Correct sale dates and hours
- [ ] Accurate property address
- [ ] Representative inventory items added

## How to Publish

1. Open the sale detail page
2. Click the **"Publish Settings"** button
3. Toggle **"Visible on Public Website"** to ON
4. Choose visibility options:
   - Show full address (or hide street number for security)
   - Show estimated item count
   - Allow pre-sale inquiries
5. Click **"Save Publishing Settings"**

## What Buyers See

Your public listing displays:
- Featured photo (first uploaded image)
- Sale title and description
- Dates and hours
- Property location (map)
- Photo gallery
- Sample inventory items
- Contact information for inquiries

## SEO and Discoverability

To help buyers find your sale:
- Use descriptive titles: "Luxury Estate Sale in Downtown" vs "Estate Sale"
- Include keywords in description: antiques, furniture, collectibles, art
- Add neighborhood/area name
- Upload clear, attractive photos
- Include notable items in description

## Publishing Timeline

**2-3 Weeks Before Sale**:
- Publish with "Coming Soon" status
- Generates early interest
- Allows time for pre-sale questions

**Week of Sale**:
- Ensure all details are accurate
- Update hours if changed
- Respond to inquiries promptly

**During Sale**:
- Keep status as "Active"
- Update if hours change
- Remove if sold out early

**After Sale**:
- Can keep published for reference
- Mark as "Completed"
- Or unpublish to remove from public view

## Unpublishing a Sale

To remove from public website:
1. Open sale detail page
2. Click "Publish Settings"
3. Toggle "Visible on Public Website" to OFF
4. Sale immediately removed from public listings

## Managing Inquiries

When published, you may receive:
- Email inquiries from buyers
- Questions about specific items
- Requests for early access
- Notification of interest

These appear in your Notifications and Contact Requests section.

## Privacy Considerations

You can control:
- Whether to show full street address
- Contact phone number visibility
- Allow/disable inquiry form
- Show/hide pricing information

## Marketing Integration

Once published, you can:
- Share direct link to sale page
- Generate QR code for flyers
- Export images for social media
- Embed sale widget on other websites

## Best Practices

- Publish at least 2 weeks in advance
- Update photos if better ones become available
- Respond to inquiries within 24 hours
- Keep sale hours accurate
- Unpublish cancelled sales immediately
- Use featured photo strategically (show best items)
        `,
        category: 'managing-sales',
        relatedArticles: ['creating-sale', 'sale-marketing', 'managing-inquiries']
      },
      {
        id: 'multi-day-sales',
        title: 'Managing Multi-Day Sales',
        lastUpdated: '2025-12-01',
        content: `
# Managing Multi-Day Sales

Most estate sales run 2-3 days. Here's how to manage them effectively in PaceEstate.

## Setting Up Multi-Day Sales

When creating a sale:
1. Set **Start Date** to first day
2. Set **End Date** to last day
3. Add **Sale Hours** for each individual day

Example for Friday-Sunday sale:
- Start: Friday, December 8, 2025
- End: Sunday, December 10, 2025
- Hours:
  - Friday: 9:00 AM - 4:00 PM
  - Saturday: 9:00 AM - 4:00 PM
  - Sunday: 12:00 PM - 3:00 PM

## Day-by-Day Inventory Tracking

### Before Day 1
- All inventory marked "Available"
- Everything visible to buyers
- Full pricing

### During Day 1
- Mark items as "Sold" as they sell
- Items automatically removed from available inventory
- Running total of sales tracked

### Between Days (Evening)
- Review sold items
- Note remaining high-value items
- Plan pricing strategy for next day

### Day 2 and Beyond
- Remaining items still shown
- Consider price reductions
- Update status of sold items

## Pricing Strategies by Day

### Day 1 (Friday)
- Full asking prices
- Premium items featured
- Best selection available

### Day 2 (Saturday)
- May offer bundle deals
- Negotiate on big furniture
- Still good pricing

### Day 3 (Sunday)
- Often "Half-Price Day"
- Bulk pricing encouraged
- Goal is to clear inventory

## Implementing Price Reductions

To mark items for reduction:
1. Go to Inventory for the sale
2. Select items to reduce
3. Click "Bulk Price Update"
4. Apply percentage discount (e.g., 50% off)
5. Items automatically recalculated

## Staff Scheduling

Assign staff for different days:
1. Open sale detail page
2. Click "Staff Assignments"
3. Assign by day:
   - Day 1: 4 staff (busiest)
   - Day 2: 3 staff
   - Day 3: 2 staff (lighter crowd)

## Daily Opening Procedure

Each morning:
1. Review prior day's sales
2. Check inventory status
3. Brief staff on featured items
4. Prepare for price adjustments (if applicable)
5. Set up payment processing

## Daily Closing Procedure

Each evening:
1. Count cash and checks
2. Record daily payments in system
3. Mark sold items
4. Secure high-value remaining items
5. Update client on progress

## Final Day Best Practices

Last day strategies:
- Announce "Last Day" pricing
- Encourage bulk purchases
- Note items for donation
- Begin planning cleanup
- Finalize all payments

## Post-Sale Wrap-Up

After last day:
1. Mark sale status as "Completed"
2. Reconcile all inventory (sold vs. remaining)
3. Record final payments
4. Schedule donation pickup
5. Generate final client invoice

## Common Multi-Day Challenges

### Challenge: Pricing confusion
**Solution**: Use clear day-specific signage, update system daily

### Challenge: Tracking sold items
**Solution**: Mark items sold immediately, assign staff to data entry

### Challenge: Staff fatigue
**Solution**: Rotate positions, schedule adequate coverage, take breaks

### Challenge: Remaining inventory
**Solution**: Plan for donations, have hauler on standby, price to move
        `,
        category: 'managing-sales',
        relatedArticles: ['pricing-strategies', 'staff-assignments', 'inventory-tracking']
      }
    ]
  },
  {
    id: 'lead-management',
    title: 'Lead Management',
    icon: 'People',
    description: 'Track and convert potential clients',
    articles: [
      {
        id: 'lead-pipeline',
        title: 'Understanding the Lead Pipeline',
        lastUpdated: '2025-12-01',
        content: `
# Understanding the Lead Pipeline

Your lead pipeline helps you track potential clients from first contact to signed contract.

## Pipeline Stages

### New
**What it means**: Fresh inquiry, not yet contacted

**Duration**: Should be 0-24 hours

**Actions Required**:
- Make first contact within 24 hours
- Qualify the lead (do they need estate sale services?)
- Gather basic information
- Schedule initial consultation if qualified

### Contacted
**What it means**: You've reached out, waiting for response or follow-up

**Duration**: 1-7 days

**Actions Required**:
- Follow up if no response in 3 days
- Answer any questions they have
- Schedule property visit
- Send information packet

### Qualified
**What it means**: Confirmed they need services and meet your criteria

**Duration**: 1-2 weeks

**Actions Required**:
- Schedule property walkthrough
- Prepare estimate
- Discuss services and pricing
- Provide references if requested

### Proposal Sent
**What it means**: You've sent pricing and contract

**Duration**: 3-10 days

**Actions Required**:
- Follow up in 3-5 days
- Answer questions about proposal
- Negotiate if necessary
- Set deadline for decision

### Won
**What it means**: Contract signed, they're now a client

**Actions**:
- Create sale record
- Move to client database
- Archive lead with "Won" status
- Begin sale planning

### Lost
**What it means**: They chose another company or decided not to proceed

**Actions**:
- Note why lost (pricing, timing, went with competitor)
- Mark as lost
- Optionally keep for future follow-up
- Learn from the loss

## Moving Leads Through Pipeline

To change a lead's stage:
1. Open the lead detail page
2. Click the current status badge
3. Select new status
4. Add notes about the change
5. Set follow-up reminder if needed

## Pipeline Best Practices

### Response Time
- New leads: Contact within 24 hours
- Follow-ups: 3-5 business days
- Proposals: Follow up 3 days after sending

### Documentation
- Note all contact attempts
- Record key conversation points
- Document client preferences
- Track objections and concerns

### Qualification Criteria
Ask yourself:
- Do they have estate sale needs?
- Is property in your service area?
- Does timeline work for your schedule?
- Are expectations realistic?

## Lead Scoring

Prioritize leads by scoring:
- **Hot**: Ready now, timeline is urgent (1-4 weeks)
- **Warm**: Interested, timeline is flexible (1-3 months)
- **Cold**: Exploratory, distant timeline (3+ months)

## Conversion Tips

### From New to Contacted
- Call within 24 hours
- Be professional and empathetic
- Listen to their situation
- Offer immediate value (free consultation)

### From Contacted to Qualified
- Ask qualifying questions
- Understand their needs and timeline
- Set clear expectations
- Establish trust

### From Qualified to Proposal
- Visit property if possible
- Provide detailed, professional proposal
- Explain your value and process
- Include references and credentials

### From Proposal to Won
- Follow up promptly
- Address concerns directly
- Be willing to negotiate reasonably
- Make signing easy (electronic signatures)

## Common Reasons Leads Stall

1. **Timing**: Not ready to commit yet
   - **Solution**: Schedule follow-up for their timeline

2. **Price Concerns**: Thinks it's too expensive
   - **Solution**: Explain value, show what's included, offer payment terms

3. **Comparison Shopping**: Talking to other companies
   - **Solution**: Highlight your unique value, don't just compete on price

4. **Emotional Difficulty**: Hard decision (parent's estate, etc.)
   - **Solution**: Be empathetic, give them time, offer support

5. **Unclear Process**: Don't understand how it works
   - **Solution**: Explain step-by-step, provide examples, show past sales

## Automated Reminders

Set automatic follow-up reminders:
- 24 hours after new lead (if not contacted)
- 3 days after sending proposal
- Weekly check-ins for long-term leads
- Monthly touches for cold leads

## Reporting

Track your pipeline health:
- **Conversion Rate**: Percentage of leads that become clients
- **Average Time to Close**: How long from lead to won
- **Stage Drop-off**: Where leads are lost most often
- **Source Performance**: Which lead sources convert best
        `,
        category: 'lead-management',
        relatedArticles: ['lead-sources', 'contact-management', 'creating-proposals']
      },
      {
        id: 'lead-sources',
        title: 'Tracking Lead Sources',
        lastUpdated: '2025-12-01',
        content: `
# Tracking Lead Sources

Understanding where your leads come from helps you focus marketing efforts on what works.

## Common Lead Sources

### Website Contact Form
- Visitors who submit inquiry through your site
- Usually high quality (found you specifically)
- Often ready to discuss services

### Phone Call
- Direct calls to your business line
- May be from various marketing sources
- Ask "How did you hear about us?"

### Referral
- Existing client recommendation
- Past client returning
- Professional referral (attorney, realtor)
- **Generally highest quality leads**

### Social Media
- Facebook, Instagram inquiries
- Comments on posts
- Direct messages
- Engagement with content

### Online Advertising
- Google Ads clicks
- Facebook/Instagram ads
- Local directory listings
- Yelp, Nextdoor, etc.

### Walk-In/Estate Sale
- Met you at a previous sale
- Saw your signs
- Picked up flyer at sale

### Networking/Events
- Chamber of Commerce
- Senior living facilities
- Professional events
- Community organizations

## Setting Lead Source

When creating a new lead:
1. Select source from dropdown
2. Add specific details in notes:
   - Which social platform?
   - Which referral source?
   - Which event?
3. Include any referral codes or campaign tags

## Why Source Tracking Matters

### Marketing ROI
- See which advertising actually brings clients
- Stop spending on sources that don't convert
- Invest more in successful channels

### Lead Quality by Source
- Referrals typically convert at 40-60%
- Website forms convert at 25-35%
- Cold advertising may convert at 5-15%

### Seasonal Patterns
- Some sources work better certain times of year
- Plan marketing calendar accordingly

## Source Reports

View lead source performance:
1. Go to **Reports** > **Lead Sources**
2. See metrics by source:
   - Total leads received
   - Conversion rate to clients
   - Average sale value
   - Time to close

## Optimizing Each Source

### Website
- Ensure form is easy to find
- Make mobile-friendly
- Fast response to submissions
- Consider chat widget

### Referrals
- Ask happy clients for referrals
- Provide referral cards to leave behind
- Consider referral incentive program
- Thank referral sources

### Social Media
- Post regularly (2-3x per week)
- Show sale photos and success stories
- Engage with comments
- Run targeted ads in your service area

### Networking
- Attend regularly
- Build relationships, don't just sell
- Provide value to connections
- Follow up after events

## Multi-Touch Attribution

Many leads touch multiple sources:
1. See Facebook post
2. Visit website
3. Call after driving by a sale

Track all touchpoints in lead notes to understand full journey.

## Source Tracking Best Practices

- Always ask "How did you hear about us?"
- Record source immediately when lead created
- Add details in notes section
- Update if you learn of additional touchpoints
- Review source reports monthly
- Adjust marketing based on data

## Red Flags

Sources to watch carefully:
- **Very low conversion**: May be wrong target audience
- **High cost per lead**: ROI may be negative
- **Poor quality contacts**: Tire kickers, not serious buyers
- **Geographic mismatch**: Leads outside service area
        `,
        category: 'lead-management',
        relatedArticles: ['lead-pipeline', 'marketing-roi', 'referral-programs']
      },
      {
        id: 'qualifying-leads',
        title: 'Qualifying Leads Effectively',
        lastUpdated: '2025-12-01',
        content: `
# Qualifying Leads Effectively

Not every inquiry becomes a client. Learn to identify and prioritize qualified leads.

## Key Qualifying Questions

### 1. Service Need
"Tell me about your situation - what type of sale are you planning?"

**Good Answers**:
- Full estate liquidation
- Downsizing/moving sale
- Inherited property cleanout
- Divorce/life transition

**Concerns**:
- Just want to sell 1-2 items (not estate sale)
- Looking for appraisal only
- Want consignment, not estate sale

### 2. Timeline
"When are you hoping to have the sale?"

**Good Answers**:
- Within next 1-3 months
- Specific deadline (house closing, move date)
- Flexible but ready to commit

**Concerns**:
- "Just gathering information"
- "Someday, no specific date"
- Urgent (this week) - may be unrealistic

### 3. Location
"Where is the property located?"

**Good Answers**:
- Within your service area
- Accessible neighborhood
- Adequate parking for buyers

**Concerns**:
- Outside service area
- Very remote location
- Unsafe neighborhood

### 4. Property Contents
"Can you describe what's in the estate?"

**Good Answers**:
- Full household of items
- Mentions furniture, collectibles, etc.
- Multiple rooms of contents
- Estimates "a lot" or "very full"

**Concerns**:
- Mostly junk or damaged items
- Already picked through
- Minimal contents

### 5. Expectations
"What are you hoping to accomplish with the estate sale?"

**Good Answers**:
- Get fair value for items
- Clear house efficiently
- Professional service
- Realistic about outcomes

**Concerns**:
- Unrealistic pricing ideas
- Expects retail prices
- Wants appraisal values
- Thinks everything is valuable

### 6. Decision Making
"Who else is involved in the decision?"

**Good Answers**:
- I'm the decision maker
- Just me and my spouse
- Family is in agreement

**Concerns**:
- Many family members must agree
- Ongoing family disputes
- Executor but family disagrees

## Qualification Criteria

### Must Have (Disqualifying if Missing)
- [ ] Actual estate sale need
- [ ] Within service area
- [ ] Timeline within 6 months
- [ ] Decision maker identified
- [ ] Contents sufficient for sale

### Should Have (Concerns but Workable)
- [ ] Realistic expectations
- [ ] Flexible on dates
- [ ] Understands commission
- [ ] Property accessible
- [ ] Minimal family conflict

### Nice to Have (Bonus Points)
- [ ] Referral from past client
- [ ] High-value items mentioned
- [ ] Professional referral source
- [ ] Urgent but reasonable timeline
- [ ] Easy decision making process

## Scoring System

Assign points to prioritize:

**Geography** (0-20 points)
- 20: In prime service area
- 10: In service area but farther
- 0: Outside service area

**Timeline** (0-20 points)
- 20: Within 1 month
- 15: 1-3 months
- 10: 3-6 months
- 0: 6+ months or unknown

**Contents** (0-20 points)
- 20: Full estate, high-value items
- 15: Full household
- 10: Partial contents
- 0: Minimal items

**Decision Process** (0-20 points)
- 20: Single decision maker
- 15: Small family, aligned
- 10: Multiple people, cooperative
- 0: Conflict or many stakeholders

**Expectations** (0-20 points)
- 20: Realistic, trusts your expertise
- 15: Reasonable, educatable
- 10: Somewhat unrealistic but open
- 0: Unrealistic, rigid

**Total Score**:
- **80-100**: Hot lead - prioritize highly
- **60-79**: Warm lead - pursue actively
- **40-59**: Lukewarm - follow up but don't chase
- **0-39**: Cold/disqualified - politely decline or low priority

## Disqualification Conversations

When you must decline a lead, be professional:

"Thank you for thinking of us. Based on what you've described, I think you might be better served by [alternative solution]. We focus on full estate liquidations in [area], and it sounds like you need [what they actually need]. I'd be happy to refer you to [alternative resource]."

Examples:
- Single item sales → Suggest auction house or consignment
- Outside area → Refer to colleague in their area
- No timeline → "Feel free to contact us when you have a date in mind"
- Unrealistic expectations → Educate gently, then decline if they won't adjust

## Follow-Up on Qualified Leads

Once qualified:
1. Move status to "Qualified"
2. Schedule property visit
3. Send information packet
4. Set follow-up reminder for 3-5 days
5. Prepare estimate/proposal

## Red Flags to Watch For

- Can't explain why they need estate sale
- Evasive about property contents
- Mentions family conflict multiple times
- Unrealistic timeline (sale in 3 days)
- Shopping for lowest commission only
- Already had multiple companies
- Won't allow property visit
- Expects you to haul everything away free
        `,
        category: 'lead-management',
        relatedArticles: ['lead-pipeline', 'first-contact', 'creating-proposals']
      }
    ]
  },
  {
    id: 'inventory',
    title: 'Inventory Management',
    icon: 'Inventory',
    description: 'Track and organize sale items',
    articles: [
      {
        id: 'adding-inventory',
        title: 'Adding Inventory Items',
        lastUpdated: '2025-12-01',
        content: `
# Adding Inventory Items

Proper inventory management is crucial for successful estate sales. Here's how to catalog items effectively.

## Creating Individual Items

1. Navigate to a sale's detail page
2. Click **"Add Inventory"** button
3. Fill out the item form:

### Required Fields
- **Description**: Clear, searchable name
  - Good: "Mahogany Queen Anne Dining Table"
  - Bad: "Table"
- **Category**: Select appropriate category
- **Price**: Your asking price
- **Status**: Usually "Available"

### Optional but Recommended
- **Location**: Room or area in house
- **Condition**: Excellent, Good, Fair, Poor
- **Notes**: Additional details, flaws, measurements
- **Photos**: Multiple angles, show condition

## Item Categories

Choose the most specific category:
- **Furniture**: Tables, chairs, beds, dressers
- **Kitchen**: Appliances, cookware, dishes
- **Collectibles**: Coins, stamps, memorabilia
- **Jewelry**: Fine jewelry, costume jewelry
- **Art**: Paintings, prints, sculptures
- **Books**: Collections, rare books
- **Electronics**: TVs, computers, audio
- **Tools**: Hand tools, power tools, workshop
- **Outdoor**: Patio furniture, garden, sports
- **Linens**: Bedding, towels, curtains
- **Clothing**: Apparel, shoes, accessories
- **Decor**: Lamps, mirrors, decorative items
- **Antiques**: Verified antique items
- **Miscellaneous**: Everything else

## Pricing Guidelines

### Research First
- Check similar items on eBay sold listings
- Consider condition (excellent = 60-80% of retail, good = 40-60%, fair = 20-40%)
- Account for local market
- Factor in urgency to sell

### Pricing Strategy
- **High-Value Items** ($100+): Research carefully, price competitively
- **Mid-Range** ($20-$100): Price to sell, allow negotiation room
- **Low-Value** (<$20): Price to move, consider bundles

### Common Pricing
- Furniture: 10-30% of original cost
- Clothing: $1-10 per item (unless designer)
- Books: $1-3 (unless rare/collectible)
- Dishes/Kitchen: 20-40% of replacement cost
- Tools: 25-50% of replacement cost

## Adding Multiple Items Quickly

### Bulk Import
For large inventories:
1. Download template (CSV file)
2. Fill in Excel or Google Sheets
3. Upload completed file
4. Review and confirm import

### Quick Add Mode
For repetitive items:
1. Enable "Quick Add" mode
2. Enter description and price
3. Press Enter to save and start next item
4. Great for books, dishes, linens

### Copy Similar Items
When you have multiples:
1. Find similar item already entered
2. Click "Duplicate"
3. Modify description and price
4. Save new item

## Item Photos

### Photo Best Practices
- Use good lighting (natural light best)
- Multiple angles for furniture
- Show any damage or flaws
- Include measurements in photo when relevant
- Clean items before photographing

### How to Add Photos
1. Open item detail
2. Click "Add Photos"
3. Select up to 6 photos per item
4. First photo becomes primary image
5. Drag to reorder photos

### When Photos Matter Most
- High-value items ($100+)
- Unique or unusual pieces
- Antiques and collectibles
- Items with condition issues
- Designer or brand-name items

## Item Location Tracking

Mark where items are located:
- Helps during sale (buyers ask "Where is...")
- Assists with staging
- Useful for multi-level homes

Example locations:
- Living Room
- Master Bedroom
- Kitchen
- Garage
- Basement
- Attic

## Item Status Options

- **Available**: For sale, ready to sell
- **Sold**: Item has been purchased
- **Reserved**: Held for potential buyer
- **Not for Sale**: Display only or client keeping
- **Donated**: Will be donated after sale
- **Damaged**: Needs repair or disclosure

## Special Handling Items

Mark items that need special attention:
- **Fragile**: Glassware, china, art
- **Heavy**: Requires help to move
- **Valuable**: Needs monitoring during sale
- **Must Sell**: Client priority items

## Search and Filter

Find items quickly:
- Search by description
- Filter by category
- Filter by price range
- Filter by location
- Filter by status

## Bulk Operations

Select multiple items to:
- Update prices (percentage off)
- Change status (mark sold)
- Update location
- Change category
- Delete items

## Inventory Reports

Generate reports showing:
- Total items by category
- Total value by category
- Items sold vs. available
- Items by location
- Price distribution

## Common Mistakes to Avoid

- Vague descriptions ("box of stuff")
- Pricing without research
- Missing photos for valuable items
- Wrong category selection
- Not noting damage/condition
- Forgetting to update status when sold
        `,
        category: 'inventory',
        relatedArticles: ['inventory-categories', 'pricing-items', 'bulk-operations']
      },
      {
        id: 'inventory-bulk-operations',
        title: 'Bulk Inventory Operations',
        lastUpdated: '2025-12-01',
        content: `
# Bulk Inventory Operations

When managing hundreds or thousands of items, bulk operations save significant time.

## When to Use Bulk Operations

- Applying discounts on specific day
- Marking multiple items sold
- Reorganizing items by location
- Updating categories in bulk
- Changing item availability

## Selecting Multiple Items

### Select All on Page
- Click checkbox in table header
- Selects all items on current page
- Useful for small batches

### Select by Filter
1. Apply filters (category, location, etc.)
2. Click "Select All Matching"
3. Selects all items matching filter
4. Works across all pages

### Manual Selection
- Check individual item boxes
- Shift+click to select range
- Command/Ctrl+click for individual additions

## Bulk Price Updates

### Percentage Discount
Common use: Half-price Sunday
1. Select items to discount
2. Click "Bulk Actions" > "Update Prices"
3. Choose "Percentage Adjustment"
4. Enter -50 for 50% off
5. Preview changes
6. Confirm update

### Fixed Amount Adjustment
1. Select items
2. Click "Bulk Actions" > "Update Prices"
3. Choose "Fixed Amount"
4. Enter +10 or -5 (positive or negative)
5. Confirm changes

### Set Specific Price
For making all similar items same price:
1. Filter to similar items (e.g., all books)
2. Select all
3. Choose "Set Price To"
4. Enter specific amount
5. Apply to all selected

### Rounding Prices
Clean up odd prices:
1. Select items
2. Choose "Round Prices"
3. Options:
   - Round to nearest $5
   - Round to nearest $10
   - Round up
   - Round down
4. Useful for easier cash handling

## Bulk Status Changes

### Marking Items Sold
End of sale:
1. Filter to "Available" items
2. Manually select items that sold
3. Bulk action: "Mark as Sold"
4. Optionally record sale price
5. Items removed from available inventory

### Moving to Donation
For unsold items:
1. Filter to "Available" after sale ends
2. Select unsold items
3. Change status to "Donated"
4. Generate donation receipt list

### Reserving Items
When buyer asks to hold multiple items:
1. Find and select their items
2. Bulk action: "Reserve Items"
3. Add note with buyer name
4. Set expiration time (e.g., end of day)

## Bulk Category Changes

When items were miscategorized:
1. Filter to incorrect category
2. Select items to recategorize
3. Bulk action: "Change Category"
4. Select correct category
5. Apply changes

## Bulk Location Updates

### Reorganizing Before Sale
Moving items for staging:
1. Select items in old location
2. Bulk action: "Update Location"
3. Select new room/area
4. Helps track items as you stage

### Room-by-Room Entry
When cataloging:
1. Work room by room
2. Add all items from one room
3. Bulk assign location after adding
4. Move to next room

## Bulk Photo Operations

### Adding Photos to Multiple Items
1. Select related items (e.g., dining set)
2. Bulk action: "Add Group Photo"
3. Upload photo showing all items
4. Photo added to all selected items

### Removing Photos
Clean up bad photos:
1. Select items with incorrect photos
2. Bulk action: "Remove Photos"
3. Confirm removal
4. Re-add correct photos individually

## Bulk Delete

### Removing Duplicate Entries
1. Identify duplicates (sort by description)
2. Select duplicate items
3. Bulk action: "Delete Items"
4. Confirm deletion (cannot undo)

### Clearing Test Data
1. Filter to test items (use naming convention)
2. Select all test items
3. Delete in bulk
4. Verify correct items selected before confirming

## Bulk Export

### Creating Reports
1. Filter to desired items
2. Select items or "Select All"
3. Bulk action: "Export to CSV"
4. Open in Excel for external analysis

### Generating Lists
Export for:
- Insurance documentation
- Donation receipts
- Client item lists
- High-value item tracking

## Safety Features

### Preview Changes
Most bulk operations show preview:
- Number of items affected
- Before/after examples
- Chance to cancel

### Undo Capability
Some operations can be undone:
- Price changes (within 1 hour)
- Status changes (within 1 hour)
- Cannot undo: Deletions

### Confirmation Prompts
System asks to confirm:
- Changes affecting 50+ items
- Deletions (any amount)
- Price changes over 50%

## Best Practices

### Before Bulk Operations
1. Test on small batch first
2. Use specific filters
3. Double-check selection count
4. Review preview before confirming

### During Bulk Operations
1. Work on one category at a time
2. Document what you're doing
3. Verify changes immediately
4. Keep client informed of major changes

### After Bulk Operations
1. Spot-check random items
2. Verify total counts make sense
3. Generate updated reports
4. Save exports as backup

## Common Bulk Operations Workflows

### End of Day 1 - 50% Off Selected Items
1. Filter: Slow-moving categories
2. Select items not sold
3. Apply 50% discount
4. Add "50% OFF" to notes
5. Generate updated price tags

### End of Sale - Final Reconciliation
1. Mark all sold items (bulk)
2. Move unsold to "Donated"
3. Export final sold report
4. Export donation list
5. Calculate totals

### Pre-Sale - Pricing Review
1. Filter: Items without prices
2. Bulk set minimum price ($1)
3. Review high-value items individually
4. Export for client approval

## Avoiding Mistakes

- Don't bulk-delete without triple checking
- Always preview price changes
- Use specific filters, not "Select All" blindly
- Keep backup export before major changes
- Test bulk import with small file first
        `,
        category: 'inventory',
        relatedArticles: ['adding-inventory', 'inventory-pricing', 'end-of-sale-procedures']
      }
    ]
  },
  {
    id: 'invoices-payments',
    title: 'Invoices & Payments',
    icon: 'Receipt',
    description: 'Billing and payment processing',
    articles: [
      {
        id: 'creating-invoices',
        title: 'Creating Client Invoices',
        lastUpdated: '2025-12-01',
        content: `
# Creating Client Invoices

Generate professional invoices for your estate sale services.

## When to Create Invoice

Create invoices:
- **After Sale Completion**: Within 24-48 hours of sale ending
- **Deposit Invoice**: When contract signed (if collecting deposit)
- **Final Invoice**: After all items sold/donated and expenses reconciled
- **Amended Invoice**: If adjustments needed after original invoice

## Invoice Creation Process

1. Navigate to **Invoices** section
2. Click **"Create Invoice"**
3. Select client and associated sale
4. Review auto-calculated amounts
5. Add any adjustments or expenses
6. Preview invoice
7. Send to client

## Invoice Components

### Automatically Calculated
- **Gross Sales Total**: Sum of all sold items
- **Commission Amount**: Gross sales × commission percentage
- **Sales Tax Collected**: If applicable
- **Net to Client**: Gross sales - commission - tax

### Manual Entries
- **Expenses**: Advertising, supplies, hauling, etc.
- **Discounts**: Special circumstances adjustments
- **Deposits Applied**: Subtract any advance payments
- **Additional Fees**: Special services if agreed

## Standard Invoice Template

\`\`\`
PaceEstate Invoice #12345
Date: December 6, 2025

Bill To:
Jane Smith
123 Estate Lane
Cityville, ST 12345

Sale Information:
Estate Sale at 123 Estate Lane
Sale Dates: November 30 - December 2, 2025

Financial Summary:
Gross Sales:                    $15,450.00
Commission (30%):               - $4,635.00
                                -----------
Subtotal:                       $10,815.00

Expenses:
  - Advertising                    -$200.00
  - Hauling/Donation Removal       -$350.00
  - Cleaning Service               -$150.00
                                -----------
Total Expenses:                    -$700.00

Deposit Applied:                 -$1,000.00

AMOUNT DUE:                      $9,115.00
\`\`\`

## Commission Calculation

Commission is typically calculated on:
- **Gross Sales**: Most common method
- **Net Sales**: After expenses (specify in contract)
- **Tiered**: Different rates for different amounts

Example Tiered Commission:
- First $10,000: 35%
- $10,001-$25,000: 30%
- Over $25,000: 25%

## Adding Expenses

Click **"Add Expense"** to itemize:

**Common Expense Categories**:
- **Advertising**: Craigslist, Facebook ads, print ads
- **Signage**: Direction signs, yard signs
- **Supplies**: Price tags, bags, tables, racks
- **Hauling**: Donation pickup, trash removal
- **Cleaning**: Post-sale cleaning service
- **Security**: If you hired security
- **Staff**: If billing for additional staff hours

**Expense Entry**:
1. Select expense category
2. Enter description
3. Enter amount
4. Attach receipt if available
5. Mark as "Billable to Client" or "Company Absorbed"

## Handling Sales Tax

If you collect sales tax for items:
- Tax collected from buyers
- Tax is NOT part of gross sales
- Tax is remitted to state
- Note on invoice: "Sales tax of $XXX collected and remitted"

If client wants tax included in commission base:
- Specify in contract
- Show calculation clearly
- Some states require commission only on pre-tax amount

## Deposit Handling

If you collected deposit:
1. Create deposit invoice when contract signed
2. Record deposit payment
3. On final invoice, subtract deposit
4. Shows: "Deposit Applied: -$1,000"
5. Client pays balance only

## Invoice Status Tracking

Invoice statuses:
- **Draft**: Created but not sent
- **Sent**: Emailed/delivered to client
- **Viewed**: Client opened email (if tracking enabled)
- **Partial**: Partially paid
- **Paid**: Fully paid
- **Overdue**: Past due date
- **Cancelled**: Voided invoice

## Sending Invoices

### Email Delivery
1. Click "Send Invoice"
2. Verify email address
3. Customize email message
4. Attach PDF invoice
5. Send (client receives immediately)

### Print/Mail
1. Click "Print Invoice"
2. Download PDF
3. Print on company letterhead
4. Mail with payment envelope

### Client Portal
Clients can:
- View invoice online
- Download PDF
- Pay online (if enabled)
- View payment history

## Payment Terms

Standard terms:
- **Due on Receipt**: Payment expected immediately
- **Net 7**: Due within 7 days
- **Net 15**: Due within 15 days
- **Net 30**: Due within 30 days

Set terms based on:
- Company policy
- Client relationship
- Contract agreement
- Industry standards

## Late Payment Handling

If payment overdue:
1. System marks "Overdue" automatically
2. Send friendly reminder at 7 days past due
3. Send second notice at 14 days past due
4. Call client at 21 days past due
5. Consider late fees if in contract

**Late Fee Example**:
- 1.5% per month on overdue balance
- Must be specified in original contract
- Add as line item to invoice or create new invoice

## Partial Payments

If client pays partial amount:
1. Record payment for amount received
2. Invoice status changes to "Partial"
3. Remaining balance shown
4. Set new due date for balance if agreed
5. Send updated statement

## Invoice Adjustments

If you need to change invoice after sending:

**Small Adjustment**:
1. Add adjustment line item
2. Explain in notes
3. Resend updated invoice

**Major Change**:
1. Cancel original invoice
2. Create new corrected invoice
3. Reference original invoice number
4. Explain changes to client

## Disputed Invoices

If client disputes charges:
1. Mark invoice as "Disputed"
2. Document client's concerns
3. Review charge accuracy
4. Provide supporting documentation
5. Negotiate resolution
6. Issue credit or adjusted invoice if appropriate

## Invoice Reports

Generate reports for:
- **Outstanding Invoices**: What's owed to you
- **Aging Report**: How long invoices unpaid
- **Revenue by Month**: Track income
- **Client Payment History**: Individual client records

## Best Practices

- Create invoices within 48 hours of sale completion
- Include detailed expense breakdown
- Attach expense receipts
- Send professionally formatted PDF
- Follow up on overdue invoices promptly
- Keep clear records of all communications
- Apply deposits correctly
- Calculate commission accurately
        `,
        category: 'invoices-payments',
        relatedArticles: ['recording-payments', 'commission-calculation', 'expense-tracking']
      },
      {
        id: 'recording-payments',
        title: 'Recording Payments',
        lastUpdated: '2025-12-01',
        content: `
# Recording Payments

Accurately track all payments received from clients and during sales.

## Types of Payments

### Client Invoice Payments
Payments from clients for your services:
- Final payment after sale
- Deposit payments
- Partial payments
- Balance payments

### Sale Day Payments
Payments from buyers during the estate sale:
- Cash payments
- Check payments
- Credit card payments
- Mobile payments (Venmo, etc.)

## Recording Client Payments

### From Invoice Page
1. Open the invoice
2. Click **"Record Payment"**
3. Enter payment details:
   - Amount received
   - Payment method
   - Payment date
   - Reference number (check #, transaction ID)
4. Add notes if needed
5. Save payment

### Payment Methods

**Cash**:
- Count carefully
- Note denomination if large bills
- Deposit promptly
- Record deposit date

**Check**:
- Record check number
- Verify name matches client
- Note bank name
- Record deposit date
- Flag if check bounces

**Credit Card**:
- Record last 4 digits
- Note transaction ID
- Account for processing fee (2-3%)
- Verify payment cleared

**Bank Transfer/ACH**:
- Record confirmation number
- Note transfer date
- Usually 2-3 day clearing time
- Verify receipt in bank

**Mobile Payment** (Venmo, Zelle, PayPal):
- Record transaction ID
- Screenshot confirmation
- Note any fees
- Verify in payment app

## Recording Sale Day Payments

During the estate sale, record buyer payments:

### Cash Register/Point of Sale
1. Navigate to the active sale
2. Click **"Record Sale"**
3. Select or scan items sold
4. Enter buyer payment amount
5. Choose payment method
6. Print receipt for buyer
7. Payment automatically recorded

### End-of-Day Reconciliation
1. Count cash drawer
2. Count checks
3. Review credit card batch
4. Enter totals in system
5. Compare to recorded sales
6. Reconcile any discrepancies
7. Prepare bank deposit

### Cash Handling Procedure
**Opening**:
- Start with set amount (e.g., $200 change)
- Record starting amount
- Separate from sales

**During Sale**:
- Keep cash secure
- Make change carefully
- Use bill checker for large bills
- Put large bills under drawer

**Closing**:
- Count all cash
- Subtract starting change amount
- Remainder = cash sales
- Verify against register tape

## Payment Status

Payments can be:
- **Pending**: Recorded but not cleared (check, ACH)
- **Cleared**: Confirmed and deposited
- **Failed**: Bounced check, declined card
- **Refunded**: Returned to customer

## Partial Payments

When client pays in installments:
1. Record each payment separately
2. System tracks total paid
3. Shows remaining balance
4. Can set up payment plan terms

### Payment Plan Example
Client owes $5,000:
- Payment 1 (deposit): $1,000 immediately
- Payment 2: $2,000 in 15 days
- Payment 3: $2,000 in 30 days

Record each as received, track in notes.

## Overpayments

If client pays too much:
1. Record full payment received
2. System shows credit balance
3. Options:
   - Refund excess
   - Apply to future invoice
   - Client may say "keep it" (tip)
4. Document decision in notes

## Failed Payments

### Bounced Check
1. Update payment status to "Failed"
2. Add returned check fee if in policy
3. Contact client immediately
4. Request alternate payment
5. May charge bounce fee ($25-35 typical)

### Declined Credit Card
1. Contact client for alternate card
2. Retry payment
3. Switch to other payment method if needed
4. Document attempts

## Payment Tracking Reports

### Accounts Receivable
Shows all unpaid invoices:
- Client name
- Invoice number
- Amount due
- Days outstanding
- Age category (current, 30+, 60+, 90+)

### Payment History
For each client:
- All invoices
- All payments
- Current balance
- Payment patterns (always late? always prompt?)

### Daily Sales Report
For active sales:
- Total cash collected
- Total checks
- Total credit cards
- Total other payments
- Grand total
- Matches inventory sold totals

### Bank Deposit Report
Prepare bank deposits:
- List all checks with numbers
- Total cash by denomination
- Credit card batch totals
- Deposit date
- Print for bank records

## Payment Allocation

When payment doesn't specify which invoice:
1. Apply to oldest invoice first (standard)
2. Or client can specify which invoice
3. Document allocation method
4. Update all affected invoices

## Refunds and Credits

### Issuing Refund
If you need to return money:
1. Create refund record
2. Link to original payment
3. Choose refund method:
   - Same method as payment
   - Check
   - Credit to account
4. Document reason
5. Process refund
6. Update invoice status

### Credit Memos
Issue credit for future use:
1. Create credit memo
2. Amount available for future invoices
3. Track credit balance
4. Apply when next invoice created

## Integration with Accounting

### QuickBooks Export
If using accounting software:
1. Export payment records
2. CSV or direct integration
3. Matches chart of accounts
4. Imports to proper categories

### End of Month
Reconcile payments:
1. Total payments recorded
2. Compare to bank deposits
3. Verify all invoices updated
4. Run accounts receivable report
5. Follow up on outstanding balances

## Security and Fraud Prevention

### Best Practices
- Count cash with two people when possible
- Verify large checks with bank before depositing
- Don't accept third-party checks
- Use card reader for credit cards (don't key manually)
- Keep payment records locked
- Limit who can record payments
- Regular audits of payment records

### Red Flags
- Check from business account for personal sale
- Out-of-state check for local sale
- Overpayment with refund request
- Insists on unusual payment method
- Rushed transaction pressure

## Payment Reminders

Automate reminders for late payments:
- **7 days past due**: Friendly reminder email
- **14 days past due**: Second notice
- **21 days past due**: Phone call
- **30 days past due**: Final notice, late fee
- **45+ days past due**: Consider collections

## Common Payment Scenarios

### Scenario 1: Client Pays at Sale End
1. Final inventory tally complete
2. Generate invoice immediately
3. Client writes check on-site
4. Record payment
5. Invoice marked paid
6. Both parties satisfied

### Scenario 2: Payment Plan
1. Client requests 3 payments
2. Create invoice with payment plan terms
3. Record deposit immediately
4. Set reminders for future payments
5. Record each payment as received
6. Follow up if payment late

### Scenario 3: Disputed Amount
1. Client questions commission calculation
2. Mark invoice "Disputed"
3. Review calculations together
4. Show contract terms
5. Adjust if error found, or explain if correct
6. Reach agreement
7. Record payment once resolved
        `,
        category: 'invoices-payments',
        relatedArticles: ['creating-invoices', 'payment-methods', 'bank-reconciliation']
      }
    ]
  },
  {
    id: 'reports-analytics',
    title: 'Reports & Analytics',
    icon: 'Assessment',
    description: 'Business insights and reporting',
    articles: [
      {
        id: 'sales-reports',
        title: 'Understanding Sales Reports',
        lastUpdated: '2025-12-01',
        content: `
# Understanding Sales Reports

PaceEstate provides comprehensive reports to analyze your business performance.

## Available Reports

### Sales Summary Report
Overview of all sales within a date range:
- Number of sales completed
- Total gross sales
- Total commission earned
- Average sale value
- Sales by status

**Use For**:
- Monthly performance review
- Year-over-year comparison
- Quarterly planning

### Sales Detail Report
Line-by-line breakdown of each sale:
- Client name
- Sale address
- Sale dates
- Gross sales amount
- Commission percentage
- Commission earned
- Expenses
- Net to client
- Payment status

**Use For**:
- Detailed financial analysis
- Client history review
- Commission verification

### Lead Conversion Report
Tracks lead pipeline performance:
- Total leads by source
- Conversion rate by source
- Average time to close
- Win/loss analysis
- Lead stage distribution

**Use For**:
- Marketing ROI analysis
- Process improvement
- Sales forecasting

### Inventory Report
Analyzes items and categories:
- Total items catalogued
- Items by category
- Sell-through rate
- Average price by category
- Unsold inventory patterns

**Use For**:
- Pricing strategy
- Category trends
- Valuation accuracy

### Financial Summary
Comprehensive financial overview:
- Total revenue
- Total commissions
- Total expenses
- Net profit
- Outstanding receivables
- Payment collection rate

**Use For**:
- Tax preparation
- Profit analysis
- Cash flow management

### Staff Performance
Individual staff metrics:
- Sales assigned
- Commission generated
- Hours worked
- Efficiency metrics
- Client feedback

**Use For**:
- Performance reviews
- Commission calculation
- Scheduling optimization

## Generating Reports

1. Navigate to **Reports** section
2. Select report type
3. Set date range:
   - This month
   - Last month
   - This quarter
   - This year
   - Custom range
4. Apply filters (if applicable):
   - Specific clients
   - Sale status
   - Staff member
   - Category
5. Click **"Generate Report"**
6. View on screen or download

## Report Formats

### On-Screen View
- Interactive tables
- Sortable columns
- Expandable sections
- Charts and graphs
- Quick filters

### PDF Export
- Professional formatting
- Company branding
- Print-ready
- Client-shareable

### Excel/CSV Export
- Raw data
- Pivot table ready
- Further analysis
- Import to accounting software

## Key Metrics Explained

### Gross Sales
Total dollar amount of items sold before commission or expenses
- Higher is better
- Varies by estate size and contents
- Benchmark: $8,000-$15,000 per sale average

### Commission Rate
Percentage charged for services
- Standard: 30-40% of gross sales
- May vary by sale size
- Document in contract

### Sell-Through Rate
Percentage of items sold vs. available
- Target: 60-70% minimum
- Excellent: 80%+
- Indicates pricing accuracy

### Average Sale Duration
Time from contract to completion
- Typical: 3-6 weeks
- Track to improve efficiency
- Identify bottlenecks

### Collection Rate
Percentage of invoices paid
- Target: 100%
- Track aging (30, 60, 90 days)
- Follow up on overdue

### Lead Conversion Rate
Percentage of leads that become clients
- Benchmark: 20-35%
- Higher = better marketing or sales process
- Track by lead source

## Using Charts and Graphs

### Sales by Month
Bar chart showing monthly revenue
- Identify seasonal patterns
- Plan for slow periods
- Set monthly goals

### Revenue by Category
Pie chart of item categories
- See what sells best
- Focus on profitable categories
- Adjust marketing

### Lead Source Performance
Comparison of lead sources
- Where to invest marketing dollars
- Which sources convert best
- ROI by channel

### Geographic Distribution
Map of sales by location
- Identify service area hot spots
- Plan marketing geographically
- Territory analysis

## Report Scheduling

Automate regular reports:
1. Select report type
2. Set frequency:
   - Daily
   - Weekly
   - Monthly
   - Quarterly
3. Choose recipients (email)
4. Report auto-generates and sends

**Suggested Schedule**:
- Weekly: Sales summary, upcoming appointments
- Monthly: Financial summary, lead conversion
- Quarterly: Comprehensive business review

## Filtering and Drill-Down

### Filter Options
Narrow reports by:
- Date range
- Client name
- Staff member
- Sale status
- Geography
- Revenue range
- Category

### Drill-Down
Click on report sections to see details:
- Click month to see sales that month
- Click category to see items
- Click staff name to see their sales
- Click lead source to see those leads

## Comparative Analysis

### Year-over-Year
Compare performance to prior year:
- Revenue growth/decline
- Volume changes
- Efficiency improvements

### Month-over-Month
Track recent trends:
- Seasonal variations
- Growth trajectory
- Recent changes impact

### Budget vs. Actual
If you set goals:
- Actual performance vs. targets
- Variance analysis
- Course correction needed

## Benchmarking

Industry benchmarks for estate sales:
- **Commission Rate**: 30-40%
- **Sell-Through**: 60-70%
- **Average Sale**: $8,000-$15,000
- **Sales per Month**: 4-8 for small company
- **Lead Conversion**: 20-35%
- **Collection Time**: 15-30 days average

Compare your metrics to these standards.

## Report Interpretation

### Strong Performance Indicators
- Increasing monthly revenue
- High sell-through rates (75%+)
- Quick collection times (<15 days)
- High lead conversion (35%+)
- Positive client feedback
- Growing repeat business

### Warning Signs
- Declining monthly revenue
- Low sell-through (<60%)
- Increasing receivables age
- Low lead conversion (<20%)
- High cancellation rate
- Long time-to-close (>8 weeks)

## Taking Action on Reports

### If Revenue Declining
- Increase marketing efforts
- Improve lead quality
- Raise commission rates
- Expand service area
- Add services

### If Sell-Through Low
- Improve pricing accuracy
- Better pre-sale research
- Enhanced marketing of sales
- Better item staging
- Extended sale hours

### If Collections Slow
- Tighten payment terms
- Require deposits
- Improve invoicing speed
- Automated payment reminders
- Offer convenient payment options

### If Lead Conversion Low
- Improve response time
- Better qualification
- Enhanced proposals
- Competitive analysis
- Sales training

## Custom Reports

Create custom reports for specific needs:
1. Choose data sources
2. Select fields to include
3. Apply filters
4. Set grouping/sorting
5. Save as template
6. Reuse for consistent analysis

## Sharing Reports

### With Clients
Appropriate to share:
- Their specific sale results
- Inventory sold vs. unsold
- Category performance
- Buyer attendance

**Do NOT share**:
- Your commission details
- Other clients' information
- Your business financials

### With Staff
Share for transparency:
- Individual performance metrics
- Team goals and progress
- Company-wide results (if appropriate)

### With Accountant
Provide for tax/accounting:
- Annual revenue reports
- Expense reports
- Payment records
- 1099 information
        `,
        category: 'reports-analytics',
        relatedArticles: ['financial-reports', 'performance-metrics', 'data-export']
      }
    ]
  },
  {
    id: 'settings-admin',
    title: 'Settings & Admin',
    icon: 'Settings',
    description: 'System configuration and administration',
    articles: [
      {
        id: 'commission-settings',
        title: 'Configuring Commission Rates',
        lastUpdated: '2025-12-01',
        content: `
# Configuring Commission Rates

Set up commission structures for your estate sale business.

## Accessing Commission Settings

1. Navigate to **Settings** (admin only)
2. Click **"Commission Settings"**
3. View current configuration

## Commission Types

### Flat Percentage
Single rate applied to all sales:
- Example: 35% of gross sales
- Simplest to understand
- Easy client communication

**When to Use**:
- Standard for most estate sales
- New business wanting simplicity
- Consistent service level

### Tiered Commission
Different rates by revenue level:
- Incentivizes larger sales
- Rewards high-value estates
- Industry common practice

**Example Structure**:
- First $10,000: 35%
- $10,001 - $25,000: 30%
- Over $25,000: 25%

**Calculation Example** for $30,000 sale:
- $10,000 × 35% = $3,500
- $15,000 × 30% = $4,500
- $5,000 × 25% = $1,250
- **Total Commission**: $9,250

**When to Use**:
- Competitive larger markets
- High-value estate focus
- Encourage accepting bigger sales

### Minimum Commission
Flat fee minimum regardless of sales:
- Example: 30% or $2,000 minimum
- Protects on small sales
- Covers base costs

**When to Use**:
- Small estates with uncertainty
- Cover minimum time investment
- High-cost service area

### Hybrid Models
Combination approaches:
- Base fee + percentage
- Hourly rate + percentage
- Tiered with minimums

## Setting Default Commission Rate

Your default rate applies to new sales:
1. Enter default percentage (e.g., 35)
2. Set whether calculated on:
   - Gross sales (standard)
   - Net sales after expenses (rare)
3. Save settings

New sales automatically use this rate, but can be adjusted per client.

## Per-Sale Custom Rates

Override default for specific clients:
1. Open sale detail
2. Click "Edit Sale"
3. Change commission rate
4. Note reason (negotiated, special case, etc.)
5. Save

**Important**: Custom rates don't change default, only affect that sale.

## Commission Calculation Settings

### What's Included in Gross Sales
Specify what counts toward commission base:
- [ ] All item sales (standard: yes)
- [ ] Sales tax collected (standard: no)
- [ ] Buyer's premium (if charged)
- [ ] Parking fees (if applicable)

### What's Excluded
Typically excluded from commission base:
- Sales tax
- Buyer's premium kept by company
- Items client removes during sale
- Donated items

### Expense Handling
Choose how expenses affect commission:
- **Option 1**: Commission on gross, expenses separate (most common)
  - Client pays: Gross - Commission - Expenses

- **Option 2**: Commission on net after expenses (rare)
  - Client pays: (Gross - Expenses) - Commission

Document clearly in contracts which method you use.

## Staff Commission

If paying staff commission:

### Staff Commission Rates
Set rates for staff members:
- Percentage of sale commission
- Example: Staff gets 10% of company commission
- Or: Staff gets 3% of gross sales

### Assignment-Based Commission
Commission only for assigned sales:
- Staff member assigned to sale
- Automatically calculates their commission
- Tracks in staff performance reports

### Split Commissions
Multiple staff on one sale:
- Define split percentages
- Example: Lead staff 60%, assistant 40%
- System calculates each person's share

## Setting Up Tiered Rates

1. Click **"Use Tiered Commission"**
2. Click **"Add Tier"** for each level
3. Enter tier details:
   - Tier 1: $0 - $10,000 at 35%
   - Tier 2: $10,001 - $25,000 at 30%
   - Tier 3: $25,001+ at 25%
4. Save configuration

System automatically calculates tiered commissions on all sales.

## Minimum Commission Setup

1. Enable **"Minimum Commission"**
2. Enter minimum amount: $2,000
3. System ensures commission never below minimum
4. Example: 35% of $5,000 = $1,750, but minimum is $2,000
   - Client charged: $2,000 commission

## Special Situation Rates

### Family/Friends Rate
- Create special rate class
- Apply to specific clients
- Example: 25% instead of 35%
- Track separately

### Non-Profit Rate
- Reduced rate for charities
- Example: 20% for qualified organizations
- Require documentation
- Track for tax purposes

### Repeat Client Rate
- Loyalty discount
- Example: 2% off for each prior sale
- Cap at maximum discount
- Automatic or manual application

## Contract Integration

Commission settings should match contracts:
1. Review contract template
2. Ensure rate language matches settings
3. Update contracts when rates change
4. Document all custom rate agreements

## Displaying Commission

### On Client Invoices
Show commission clearly:
\`\`\`
Gross Sales:           $15,000.00
Commission (35%):      - $5,250.00
\`\`\`

### On Internal Reports
Track commission separately:
- Commission earned
- Commission paid to staff
- Net commission to company

## Commission Adjustments

### After Sale Adjustments
If needed to adjust commission:
1. Open invoice
2. Add adjustment line item
3. Explain reason:
   - Calculation error
   - Negotiated reduction
   - Special circumstance
4. Document in notes
5. Save adjusted invoice

### Partial Commission Waivers
Rare situations waiving part of commission:
- Extremely low sales (goodwill)
- Long-term client relationship
- Make-good for service issue
- Document thoroughly

## Reporting on Commission

### Commission Reports
View commission data:
- Total commission by period
- Commission by sale
- Commission by staff member
- Average commission rate
- Commission trends

### Tax Reporting
Commission is taxable income:
- Include in revenue reports
- Track for quarterly taxes
- Provide to accountant
- 1099 for contractors paid commission

## Best Practices

### Setting Rates
- Research local market rates
- Cover your costs plus profit
- Be competitive but fair
- Value your expertise
- Don't undervalue services

### Communicating Rates
- State clearly in marketing
- Discuss upfront with leads
- Put in writing (contract)
- No hidden fees
- Transparent calculations

### Adjusting Rates
- Review annually
- Increase with experience/reputation
- Grandfather existing contracts
- Give clients notice
- Justify increases (added value)

## Common Commission Structures

### Standard Estate Sale
- 30-40% of gross sales
- 35% is industry average
- No minimum on standard estates

### High-End Estate
- 25-30% (lower rate, higher volume)
- May have minimum ($5,000+)
- Tiered structure common

### Small Estate/Cleanout
- 40-50% or flat fee
- Minimum commission required
- Higher rate due to time investment

### Online Estate Sale
- 40-50% (higher due to effort)
- May include listing fees
- Shipping handling fees separate
        `,
        category: 'settings-admin',
        relatedArticles: ['tax-settings', 'pricing-strategy', 'client-contracts']
      },
      {
        id: 'tax-settings',
        title: 'Sales Tax Configuration',
        lastUpdated: '2025-12-01',
        content: `
# Sales Tax Configuration

Configure sales tax settings for compliance with your state and local regulations.

## Understanding Sales Tax for Estate Sales

### Tax Collection Requirements
Most states require sales tax on:
- Personal property sales
- Estate sale transactions
- Goods sold to end consumers

### Exemptions
Typically exempt from sales tax:
- Items under certain dollar amount (varies by state)
- Specific categories (groceries, prescription items)
- Sales to tax-exempt organizations
- Certain collectibles or antiques (varies by state)

**Important**: Tax laws vary significantly by state and locality. Consult a tax professional for your specific situation.

## Accessing Tax Settings

1. Navigate to **Settings** > **Tax Settings**
2. Admin access required
3. Review current configuration

## Setting Default Tax Rate

### Find Your Tax Rate
Determine your combined rate:
1. State sales tax rate
2. County sales tax rate
3. City/local sales tax rate
4. Special district rates

Example:
- State: 6.0%
- County: 1.0%
- City: 1.5%
- **Total: 8.5%**

### Configure in System
1. Enter combined tax rate: 8.5
2. System applies to all taxable sales
3. Can override per sale if needed

## Tax by Location

If you operate in multiple jurisdictions:

### Enable Location-Based Tax
1. Toggle **"Location-Based Tax Rates"**
2. Add each service area:
   - Enter ZIP code or city
   - Enter applicable tax rate
   - Save location

### How It Works
- Create sale with address
- System detects location
- Applies correct tax rate automatically
- Shows rate on invoice

Example:
- Downtown sales: 8.5%
- County sales: 7.0%
- Neighboring city: 8.0%

## Tax Calculation Methods

### Tax on Full Amount (Most Common)
Tax calculated on sale price:
- Item: $100
- Tax (8.5%): $8.50
- Total: $108.50

### Tax Included in Price (Rare)
Price includes tax:
- Advertised: $100
- Actual price: $92.17
- Tax: $7.83
- Total: $100.00

**Note**: If using tax-included pricing, clearly mark items "Tax Included" to comply with regulations.

## Exempt Items

### Setting Category Exemptions
If certain categories are exempt:
1. Go to **Inventory Categories**
2. Select category (e.g., "Books")
3. Check **"Tax Exempt"**
4. Save

Items in that category won't have tax applied.

### Common Exemptions by State
(Examples - verify your state laws):
- **Clothing**: Exempt in some states
- **Food**: Exempt if not prepared
- **Prescription Items**: Universally exempt
- **Resale Items**: Exempt with resale certificate

### Item-Level Exemption
Override for specific items:
1. Edit item
2. Check **"Tax Exempt"**
3. Note exemption reason
4. Save

## Tax on Commission

### Commission Tax Treatment
In most states:
- Commission is your service fee
- Commission is your taxable income (for you)
- You don't collect sales tax on your commission
- You collect sales tax only on items sold

### Example
- Item sells for: $100
- Sales tax (8.5%): $8.50
- Total buyer pays: $108.50
- Your commission (35%): $35.00 (on the $100)
- Client receives: $65.00
- **Tax collected**: $8.50 (remitted to state)

Commission ($35) is income to you, reported on your taxes, but not subject to sales tax collection.

## Tax Reporting

### Daily Sales Tax Report
For each sale:
- Total sales
- Tax collected
- Tax rate applied
- Tax by category

### Monthly Tax Report
For tax filing:
1. Navigate to **Reports** > **Tax Report**
2. Select month/quarter
3. View:
   - Total taxable sales
   - Total tax collected
   - Breakdown by location/rate
   - Exempt sales total
4. Export for filing

### State Tax Filing
Use exported reports to file:
- Monthly state sales tax return
- Quarterly if low volume
- Include all sales locations
- Remit tax collected

## Resale Certificates

### When Buyers Have Resale Certificates
If buyer is reseller (dealer, etc.):
1. Collect resale certificate
2. Verify certificate valid
3. Mark sale as "Resale - Tax Exempt"
4. Keep certificate on file (7 years typically)
5. No tax charged on that sale

### Recording Resale Sales
1. Process sale normally
2. Apply **"Resale Exemption"**
3. Attach certificate copy
4. Track separately for reports

## Tax Compliance

### Required Records
Maintain for audit:
- All sales records with tax calculated
- Tax collected by period
- Exemption certificates
- Tax return filings
- Payment confirmations

### Audit Preparedness
If audited:
- Pull tax reports by date range
- Show exempt sales with documentation
- Demonstrate rate accuracy
- Provide payment proof

### Penalties for Non-Compliance
Failure to collect/remit sales tax:
- Back taxes owed
- Penalties (25-50% common)
- Interest on unpaid amounts
- Potential license revocation

## Multi-State Sales

### Operating in Multiple States
If you conduct sales in different states:
- May need to register in each state
- Collect tax at rate where sale occurs
- File returns in each state
- Track separately

### Online/Shipping Sales
If shipping items to buyers:
- Tax based on destination (usually)
- Complex nexus rules
- Consider marketplace facilitator rules
- Consult tax professional

## Tax Rate Changes

### Updating Rates
When local rates change:
1. Update in **Tax Settings**
2. Set effective date
3. System applies new rate going forward
4. Historical sales keep old rate

### Notification
- System can notify you of rate changes
- Based on your service area ZIP codes
- Verify with official sources

## Common Tax Scenarios

### Scenario 1: Standard Taxable Sale
- Item price: $50
- Tax rate: 8.5%
- Tax: $4.25
- Total: $54.25
- Commission: 35% of $50 = $17.50
- Client gets: $32.50
- Tax remitted to state: $4.25

### Scenario 2: Mix of Taxable and Exempt
- Taxable items: $500 (tax: $42.50)
- Exempt books: $100 (tax: $0)
- Total sale: $600
- Total tax: $42.50
- Total buyer pays: $642.50

### Scenario 3: Resale Certificate
- Sale amount: $1,000
- Buyer provides valid resale certificate
- Tax: $0 (exempt)
- Total: $1,000
- Certificate kept on file

## Tax Settings Checklist

- [ ] Determine combined tax rate for primary location
- [ ] Configure default tax rate
- [ ] Set up additional locations if needed
- [ ] Identify exempt categories
- [ ] Set up tax reporting schedule
- [ ] Document tax collection policy
- [ ] Train staff on tax procedures
- [ ] Establish certificate collection process
- [ ] Schedule regular compliance reviews

## Getting Help

Sales tax can be complex:
- **State Revenue Department**: Official guidance
- **Tax Professional**: CPA or tax attorney
- **Industry Association**: Estate sale best practices
- **PaceEstate Support**: Technical system questions

**Important**: This guide is informational. Always consult a tax professional for compliance advice specific to your business and location.
        `,
        category: 'settings-admin',
        relatedArticles: ['commission-settings', 'financial-reports', 'compliance']
      }
    ]
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: 'Build',
    description: 'Common issues and solutions',
    articles: [
      {
        id: 'login-issues',
        title: 'Cannot Log In',
        lastUpdated: '2025-12-01',
        content: `
# Cannot Log In

Having trouble accessing your PaceEstate account? Here are solutions to common login problems.

## Forgot Password

### Reset Your Password
1. Go to login page
2. Click **"Forgot Password?"**
3. Enter your email address
4. Check your email for reset link
5. Click link in email (valid for 1 hour)
6. Enter new password
7. Confirm new password
8. Click "Reset Password"
9. Log in with new password

### Didn't Receive Reset Email
- Check spam/junk folder
- Verify email address is correct
- Wait 5 minutes (may be delayed)
- Try resend
- Contact your administrator

## Incorrect Password

### Common Issues
- **Caps Lock is on**: Check keyboard indicator
- **Typing wrong password**: Try carefully retyping
- **Old password**: Recently changed? Use new one
- **Auto-fill incorrect**: Clear and type manually

### Account Locked
After 5 failed attempts:
- Account locked for 15 minutes
- Wait and try again
- Or request password reset
- Contact admin if urgent

## Email Not Recognized

### Possible Reasons
- **Typo in email**: Double-check spelling
- **Wrong email**: Using personal instead of work?
- **Account not created**: Contact administrator
- **Account deactivated**: Contact administrator

## Browser Issues

### Clear Cache and Cookies
1. Browser Settings > Privacy
2. Clear browsing data
3. Select "Cookies" and "Cached images"
4. Clear last 24 hours
5. Close browser completely
6. Reopen and try login

### Try Different Browser
- Chrome (recommended)
- Firefox
- Safari
- Edge

### Disable Browser Extensions
- Ad blockers may interfere
- Privacy extensions may block
- Disable temporarily to test

## Connection Problems

### Check Internet Connection
- Verify you're online
- Try loading another website
- Restart router if needed
- Try different network (mobile hotspot)

### Firewall/VPN Issues
- Company firewall may block
- VPN may need configuration
- Contact IT department

## Mobile App Issues

### Update the App
1. Check app store for updates
2. Install latest version
3. Restart device
4. Try logging in again

### Clear App Data
**iOS**:
1. Delete app
2. Reinstall from App Store
3. Log in fresh

**Android**:
1. Settings > Apps > PaceEstate
2. Clear Data and Cache
3. Reopen app

## Account Status Issues

### Account Pending
- New accounts may need admin approval
- Check with your administrator
- Wait for activation email

### Account Suspended
- Unusual activity detected
- Billing issue (if applicable)
- Policy violation
- Contact administrator

### Account Deleted
- Cannot recover deleted account
- Administrator must create new account
- Previous data may be lost

## Still Can't Log In?

### Contact Your Administrator
Provide:
- Your email address
- Error message you're seeing
- When problem started
- Steps you've tried

### Contact Support
If admin cannot help:
- Email: support@paceestate.com
- Include:
  - Company name
  - Your email
  - Screenshot of error
  - Browser and version

## Security Best Practices

### Create Strong Password
- At least 8 characters
- Mix of letters, numbers, symbols
- Avoid common words
- Don't reuse from other sites
- Use password manager

### Keep Password Secure
- Don't share with others
- Don't write it down visibly
- Don't email it
- Change if compromised
- Log out on shared computers

### Enable Two-Factor Authentication
If available:
- Adds extra security layer
- Requires phone verification
- Highly recommended
        `,
        category: 'troubleshooting',
        relatedArticles: ['account-settings', 'security-best-practices', 'browser-compatibility']
      },
      {
        id: 'data-not-loading',
        title: 'Data Not Loading or Displaying',
        lastUpdated: '2025-12-01',
        content: `
# Data Not Loading or Displaying

If you're experiencing issues with data not appearing or loading slowly, try these solutions.

## Refresh the Page

### Simple Refresh
- Press **F5** (Windows) or **Cmd+R** (Mac)
- Or click browser refresh button
- Wait for page to fully reload

### Hard Refresh (Clear Cache)
- **Windows**: Ctrl+F5 or Ctrl+Shift+R
- **Mac**: Cmd+Shift+R
- Forces reload of all resources

## Check Your Internet Connection

### Connection Speed
- Slow connection = slow loading
- Test speed at speedtest.net
- Minimum recommended: 5 Mbps

### Connection Stability
- Intermittent connection causes issues
- Try different network
- Restart router
- Contact ISP if persistent

## Browser-Related Issues

### Clear Browser Cache
1. Browser Settings
2. Privacy/History section
3. Clear cache and cookies
4. Select "All time" or "Last hour"
5. Restart browser
6. Log back in

### Update Browser
- Use latest browser version
- Chrome, Firefox, Safari, Edge all supported
- Older browsers may have compatibility issues

### Disable Extensions
Temporarily disable:
- Ad blockers
- Privacy extensions
- Script blockers
- VPN extensions

Test if data loads with extensions disabled.

## Specific Data Not Loading

### Dashboard Statistics
If stats show zeros or dashes:
- May be calculating
- Wait 30 seconds and refresh
- Check date filter (data for that period?)
- Verify you have permission to see data

### Inventory Not Appearing
- Check status filter (showing "All"?)
- Check category filter
- Search may be too specific
- Try clearing all filters

### Sales List Empty
- Verify date range filter
- Check status filter (include all statuses)
- Verify you have sales assigned
- Admin can see all, staff see assigned only

### Reports Not Generating
- Large reports take time (30-60 seconds)
- Don't click multiple times
- Watch for progress indicator
- Try smaller date range

## Performance Issues

### Page Loading Slowly
Common causes:
- Large dataset (1000+ items)
- Many photos loading
- Complex reports
- Slow internet

Solutions:
- Use filters to reduce data
- Paginate (20-50 items per page)
- Remove date range restriction
- Optimize photos (resize large images)

### Table Not Sorting
- Try clicking column header again
- Refresh page
- Clear browser cache

### Search Not Working
- Check spelling
- Try partial words
- Remove special characters
- Try broader search terms
- Clear search and retry

## Photo and File Issues

### Images Not Loading
- Check internet connection
- Images may be large (slow to load)
- Right-click > Open in new tab to test
- Try different browser

### PDF Documents Not Opening
- Ensure PDF viewer enabled in browser
- Download file instead of preview
- Update Adobe Reader
- Try different browser

### Upload Failing
- File may be too large (limit: 10MB per file)
- Wrong file format
- Check internet connection
- Try smaller file
- Compress images before upload

## Session Timeout

### Automatically Logged Out
- Sessions expire after inactivity
- Default: 60 minutes
- Data entry may be lost
- Save work frequently

### Stay Logged In
- Don't leave browser idle for long periods
- Periodically click or refresh
- Save drafts regularly

## Error Messages

### "Something went wrong"
Generic error:
1. Refresh page
2. Clear cache
3. Try different browser
4. Contact support with details

### "Permission denied"
- Action requires admin access
- Contact administrator
- May need role upgraded

### "Not found"
- Item may have been deleted
- URL may be incorrect
- Go back and try navigating again

### "Network error"
- Connection lost
- Firewall blocking
- Try different network

## Database Synchronization

### Data Seems Out of Date
- Click refresh icon
- Auto-refresh every 5 minutes
- Manual refresh for instant update

### Changes Not Saving
- Check for error message
- Verify required fields completed
- Try saving again
- Check internet connection

### Duplicate Entries Appearing
- May be double-clicking "Save"
- Click once and wait
- Refresh to see if duplicate
- Delete duplicate if created

## Mobile-Specific Issues

### App Not Loading Data
- Check mobile data/WiFi
- Force close and reopen app
- Update app to latest version
- Clear app cache
- Reinstall app if needed

### Responsive Layout Issues
- Rotate device
- Zoom in/out
- Try desktop site mode
- Use tablet/computer for complex tasks

## Advanced Troubleshooting

### Developer Console Errors
For technical users:
1. Press F12 to open console
2. Look for red errors
3. Screenshot errors
4. Send to support

### Network Tab
1. F12 > Network tab
2. Refresh page
3. Look for failed requests (red)
4. Note which requests failed
5. Report to support

## When to Contact Support

Contact support if:
- Issue persists after trying above
- Error message you don't understand
- Data loss or corruption suspected
- System-wide outage suspected
- Urgent business need

### Information to Provide
- Exact error message
- Screenshot of issue
- Steps you've tried
- Browser and version
- When problem started
- Frequency (always, sometimes, once)
        `,
        category: 'troubleshooting',
        relatedArticles: ['login-issues', 'browser-compatibility', 'performance-optimization']
      }
    ]
  }
];

// Helper function to get all articles across all categories
export const getAllArticles = () => {
  const articles = [];
  helpCategories.forEach(category => {
    category.articles.forEach(article => {
      articles.push({
        ...article,
        categoryId: category.id,
        categoryTitle: category.title
      });
    });
  });
  return articles;
};

// Helper function to search articles
export const searchArticles = (query) => {
  if (!query || query.trim().length < 2) return [];

  const searchTerm = query.toLowerCase();
  const allArticles = getAllArticles();

  return allArticles.filter(article => {
    return (
      article.title.toLowerCase().includes(searchTerm) ||
      article.content.toLowerCase().includes(searchTerm) ||
      article.categoryTitle.toLowerCase().includes(searchTerm)
    );
  });
};

// Helper function to get article by ID
export const getArticleById = (articleId) => {
  const allArticles = getAllArticles();
  return allArticles.find(article => article.id === articleId);
};

// Helper function to get category by ID
export const getCategoryById = (categoryId) => {
  return helpCategories.find(category => category.id === categoryId);
};

// Helper function to get related articles
export const getRelatedArticles = (articleId) => {
  const article = getArticleById(articleId);
  if (!article || !article.relatedArticles) return [];

  return article.relatedArticles
    .map(id => getArticleById(id))
    .filter(Boolean);
};
